import { atom, useAtom } from 'jotai';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useEventCallback } from '@gitbook/util-react-hooks';

export enum ChangeSitePlanDialogLayout {
    Regular = 'regular',
    Branded = 'branded',
}

export type ChangeSitePlanDialogState = {
    /** The site for which the plan change dialog is being shown */
    site: api.Site;
    /** Optional site type override to pre-select a plan */
    siteTypeOverride?: api.SiteType;
    /** The organization that owns the site */
    organization: api.Organization;
    /** The layout variant for the dialog */
    layout: ChangeSitePlanDialogLayout;
    /** The reason for opening the dialog (for analytics) */
    reason: api.SiteUpgradeReason;
};

const changeSitePlanDialogState = atom<ChangeSitePlanDialogState | null>(null);

/**
 * Hook to manage the change site plan dialog state.
 * Provides functions to open and close the dialog, along with access to the current dialog state.
 */
export function useChangeSitePlanDialog(): Partial<ChangeSitePlanDialogState> & {
    openChangeSitePlanDialog: (params: ChangeSitePlanDialogState) => void;
    closeChangeSitePlanDialog: () => void;
} {
    const [state, setState] = useAtom(changeSitePlanDialogState);

    const openChangeSitePlanDialog = useEventCallback((params: ChangeSitePlanDialogState) => {
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: params.site.id, organization: params.organization.id },
                {
                    type: 'site_open_change_plan_dialog',
                    reason: params.reason,
                    currentPlan: params.site.appliedType,
                    selectedPlan: params.siteTypeOverride ?? params.site.appliedType,
                }
            )
        );
        setState({
            site: params.site,
            siteTypeOverride: params.siteTypeOverride,
            organization: params.organization,
            layout: params.layout,
            reason: params.reason,
        });
    });

    const closeChangeSitePlanDialog = useEventCallback(() => {
        setState(null);
    });

    return {
        site: state?.site,
        siteTypeOverride: state?.siteTypeOverride,
        organization: state?.organization,
        layout: state?.layout,
        reason: state?.reason,
        openChangeSitePlanDialog,
        closeChangeSitePlanDialog,
    };
}
